import React, { useRef, useState, useEffect, useCallback } from 'react';
import { SoundOutlined, SoundFilled } from '@ant-design/icons';
import { SeriesProps } from '../../types/series';
import { useARCallbacks } from '../../hooks/useARCallbacks';
import { useARScene } from '../../hooks/useARScene';
import { ARScene, ARImageTarget } from '../../components/ARScene';
import { getPaintingText, patchPaintingText } from '../../api/api';
import './LovePainting.css';
import { Modal } from '../../components/Modal';
import { ARScanner } from '../../components/ARScanner';
import { IconButton } from '../../components/IconButton';
import { useMusicPlayer } from '../../hooks/audio/useMusicPlayer';
import { SubHeader } from '../../components/SubHeader';

const TARGET_JSON = '/paintings/love/targets/love-target.json';

function getDynamicFontSize(text: string | null): number {
    if (!text || text.length === 0) return 0.12;
    const symbolsPerMeter = 700;
    const min = 0.06;
    const max = 0.09;
    const size = symbolsPerMeter / text.length / 100;
    return Math.max(min, Math.min(max, size));
}

export const LovePainting: React.FC<SeriesProps> = ({ id }) => {
    const sceneRef = useRef<HTMLElement>(null);
    const { engine, ready, targetName, mindTargetUrl } = useARScene({
        targetJsonUrl: TARGET_JSON,
        sceneRef,
    });
    const [text, setText] = useState<string | null>(null);
    const textRef = useRef<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isTargetFound, setIsTargetFound] = useState(false);
    const [isArReady, setIsArReady] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const autoModalShown = useRef(false);
    const autoModalTimeout = useRef<NodeJS.Timeout | null>(null);
    const { isPlaying, play, pause, toggle, cleanup, getMusicEnabled } = useMusicPlayer('/paintings/love/music/music-love.mp3');
    const [musicStarted, setMusicStarted] = useState(false);
    const location = typeof window !== 'undefined' ? window.location : { origin: '' };
    const editorUrl = `${location.origin}/painting/${id}/editor`;

    const updateText = useCallback((newText: string | null) => {
        setText(newText);
        textRef.current = newText;
    }, []);

    const updateARText = useCallback((value: string | null) => {
        if (sceneRef.current) {
            const textTitle = sceneRef.current.querySelector('#text_title');
            if (textTitle) {
                textTitle.setAttribute('value', value || '');
                textTitle.setAttribute('font-size', getDynamicFontSize(value).toString());
            }
        }
    }, []);

    const handleSaveText = useCallback(async (newText: string | null) => {
        try {
            const data = await patchPaintingText(id, newText);
            console.log('[LovePainting] Updated painting text:', data);
            updateText(data.text);
            updateARText(data.text);
        } catch (error) {
            console.error('Error updating painting text:', error);
        }
    }, [id, updateText, updateARText]);

    useEffect(() => {
        const fetchPaintingText = async () => {
            try {
                const data = await getPaintingText(id);
                console.log('[LovePainting] Painting text:', data);
                updateText(data.text);
            } catch (error) {
                console.error('Error fetching painting text:', error);
            }
        };
        fetchPaintingText();
    }, [id, updateText]);

    useEffect(() => cleanup, [cleanup]);

    useARCallbacks({
        sceneRef,
        targetName,
        engine,
        enabled: ready,
        callbacks: {
            arReady: () => setIsArReady(true),
            arLoaded: () => {},
            arError: (error: Error) => console.error('[LovePainting] AR error:', error),
            targetFound: () => {
                setIsTargetFound(true);
                setShowContent(true);
                if (!musicStarted && getMusicEnabled()) {
                    play();
                    setMusicStarted(true);
                }
                if ((textRef.current === null || textRef.current === '') && !autoModalShown.current) {
                    autoModalTimeout.current = setTimeout(() => {
                        setIsModalOpen(true);
                        autoModalShown.current = true;
                    }, 3000);
                }
                updateARText(textRef.current);
            },
            targetLost: () => {
                setIsTargetFound(false);
                setShowContent(false);
                pause();
                if (autoModalTimeout.current) {
                    clearTimeout(autoModalTimeout.current);
                    autoModalTimeout.current = null;
                }
            },
        },
    });

    return (
        <div className="augmented_layout">
            <ARScanner isVisible={!isTargetFound && isArReady} />
            <IconButton
                type="text"
                onClick={() => setIsModalOpen(true)}
                className={isTargetFound ? 'icon-button--visible' : 'icon-button--hidden'}
            />
            {isTargetFound && (
                <IconButton
                    type={isPlaying ? 'music-on' : 'music-off'}
                    onClick={toggle}
                    className="music-icon-button"
                    icon={isPlaying ? <SoundFilled className="icon-button__icon" /> : <SoundOutlined className="icon-button__icon" />}
                />
            )}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                mode="text"
                value={text}
                onSave={handleSaveText}
                subHeader={<SubHeader url={editorUrl} />}
                title={`Редактировать текст`}
                placeholder="Введите текст для картины"
            />
            <ARScene
                engine={engine}
                ready={ready}
                mindTargetUrl={mindTargetUrl}
                sceneRef={sceneRef}
            >
                <a-assets>
                    <img
                        id="resource_frame"
                        src="/paintings/love/images/frame.webp"
                        alt="Resource frame"
                    />
                    <img
                        id="resource_heart"
                        src="/paintings/love/images/heart.webp"
                        alt="Resource heart"
                    />
                    <a-asset-item
                        id="adleryFont"
                        src="/paintings/shared/fonts/adlery.woff"
                    />
                </a-assets>
                <a-camera
                    position="0 0 0"
                    look-controls="enabled: false"
                />
                <ARImageTarget engine={engine} targetName={targetName}>
                    <a-image
                        id="image_frame"
                        src="#resource_frame"
                        width="1.2"
                        height="1.2"
                        position={`0 0 0`}
                        animation__fade={`property: opacity; to: ${showContent ? 1 : 0}; dur: 500; easing: easeInOutSine`}
                    />
                    <a-image
                        id="image_heart"
                        src="#resource_heart"
                        width="0.75"
                        height="0.75"
                        position={`0 0 0.001`}
                        animation__fade={`property: opacity; to: ${showContent ? 1 : 0}; dur: 500; easing: easeInOutSine`}
                    />
                    <a-troika-text
                        id="text_title"
                        value=""
                        color="#000000"
                        position={`0 0.05 0.002`}
                        max-width="0.5"
                        font-size={getDynamicFontSize(text)}
                        baseline="center"
                        align="center"
                        font="/paintings/shared/fonts/adlery.woff"
                        animation__fade={`property: opacity; to: ${showContent ? 1 : 0}; dur: 500; easing: easeInOutSine`}
                    />
                </ARImageTarget>
            </ARScene>
        </div>
    );
};
